import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CustomAvatar from "../CustomAvatar";
import Editor from "../Editor/Editor";
import HTMLParser from "../HTMLParser/HTMLParser";
import BtButton from "../../../ts-components/bt-button/bt-button";
import { ReactComponent as EditIcon } from "../../../assets/svg/editIcon.svg";
import { TrashIcon } from "../../svgs";
import { reportConversationError } from "./reportConversationError";
import styles from './ConversationModal.module.scss';

type Props = {
  content: string,
  authorName: string,
  authorAvatarUrl: string,
  date: string,
  updatedAt?: string,
  minified?: boolean,
  canEdit?: boolean,
  onSave?: (newContent: string) => Promise<void>,
  onDelete?: () => Promise<void>,
}

// The single rendering of "one comment" — reused by ConversationModal's list (purchase-plan
// comments, supplier comments) and by the per-supplier phone-log modal, so the hover/edit/delete
// UI (mirroring ContactRow's pattern) only has to be built once.
const ConversationMessage: FC<Props> = ({ content, authorName, authorAvatarUrl, date, updatedAt, minified, canEdit, onSave, onDelete }) => {
  const [t] = useTranslation('shared', { keyPrefix: 'conversationModal' });
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [isSaving, setIsSaving] = useState(false);

  const getFormattedDate = (date: string) => {
    const momentDate = moment(date);
    const timeDifference = momentDate.diff(moment(), 'seconds') * -1;

    if (timeDifference === 0) return t('timestamp.now', 'Nu');
    if (timeDifference >= 60*60*24) return momentDate.format('YYYY-MM-DD HH:mm'); // More than a day

    return momentDate.fromNow();
  }

  // Reporting lives here rather than in each caller so both surfaces behave the same: the phone log
  // passes its save straight through and used to lose the rejection entirely, while the modal
  // caught it and resolved, which read as success and closed the editor over the user's text.
  const handleSave = async () => {
    if (!editedContent || !onSave) return;

    setIsSaving(true);
    try {
      await onSave(editedContent);
      setIsEditing(false);
    } catch (error) {
      // Stay in edit mode. The typed text exists nowhere but editedContent, and the server did not
      // apply it, so closing would discard the only copy.
      reportConversationError(t, error);
    } finally {
      setIsSaving(false);
    }
  }

  // Seeded from whatever content is at the moment the editor opens, not from the render that first
  // mounted the row: useState only takes its initial value once, so a draft left behind by an
  // earlier edit outlived every later change to content and would be saved back over it.
  const handleEdit = () => {
    setEditedContent(content);
    setIsEditing(true);
  }

  const handleCancel = () => {
    setEditedContent(content);
    setIsEditing(false);
  }

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm(t('confirm.delete', 'Är du säker på att du vill ta bort kommentaren?'))) return;

    try {
      await onDelete();
    } catch (error) {
      reportConversationError(t, error);
    }
  }

  if (isEditing) {
    return (
      <div className={styles.message}>
        {authorName && <CustomAvatar alt={authorName} src={authorAvatarUrl} size={"32px"} />}
        <div className={styles.editForm}>
          <Editor value={editedContent} onChange={setEditedContent} wrapperStyle={{ width: '100%', height: '150px' }} />
          <div className={styles.editActions}>
            <BtButton color="white" size="xxs" onClick={handleSave} loaderShow={isSaving} disabled={!editedContent}>
              {t('button.save', 'Spara')}
            </BtButton>
            <BtButton color="white" size="xxs" onClick={handleCancel} disabled={isSaving}>
              {t('button.cancel', 'Avbryt')}
            </BtButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.message} ${minified ? styles.minified : ''} ${canEdit ? styles.hoverable : ''}`}>
      {!minified && authorName && (
        <CustomAvatar
          alt={authorName}
          src={authorAvatarUrl}
          size={"32px"}
        />
      )}
      <div className={styles.messageDetails}>
        <div>
          <div>
            {!minified && authorName && <span className={styles.name}>{authorName} </span>}
            {!minified && (
              <span className={styles.date}>
                {getFormattedDate(date)}
                {updatedAt && updatedAt !== date && ` (${t('edited', 'redigerad')})`}
              </span>
            )}
          </div>
          <HTMLParser data={content} />
        </div>
      </div>
      {canEdit && (
        <div className={styles.messageActions}>
          {onSave && (
            <BtButton
              class={styles.editIcon}
              ariaLabel={t('button.edit', 'Redigera')}
              title={t('button.edit', 'Redigera')}
              tabIndex={0}
              onClick={handleEdit}
            >
              <EditIcon />
            </BtButton>
          )}
          {onDelete && (
            <BtButton
              class={styles.removeIcon}
              ariaLabel={t('button.delete', 'Ta bort')}
              title={t('button.delete', 'Ta bort')}
              tabIndex={0}
              onClick={handleDelete}
            >
              <TrashIcon />
            </BtButton>
          )}
        </div>
      )}
    </div>
  )
}

export default ConversationMessage;
